/**
 * 系統狀態管理 Context
 * 包含系統設定和語系資訊
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemProfile, I18nField } from '../types';
import { systemService, LicenseInfo } from '../api/systemService';
import { getI18nValue } from '../utils/i18nHelper';
import { setDisplayTimeZone } from '../utils/dateHelper';
import axios from '../api/axios';
import { onSuspend } from '../api/suspendSignal';

// 語系選項
export interface LanguageOption {
  code: string;
  cname: string;
  ename: string;
  short_name: string;
}

// 租戶 branding（public /api/system/tenant-branding，依 Host 解析；login 前即生效）
export interface TenantBranding {
  system_name: Record<string, string>;
  tenant_copyright: Record<string, string>;
  tenant_languages: string[];
  theme_color: string;
  layout_mode: string;
  logo_file_id: number | null;
  login_bg_file_id: number | null;
  favicon_file_id: number | null;
  timezone: string;
  is_service_active: boolean;   // Layer 2 租戶服務開關（FALSE → MaintenancePage）
  enabled_ui_modes?: string[];  // Sprint 5-d PR-5：介面模式（function / chat）·chat mode gate 條件 2
}

interface SystemContextType {
  systemProfile: SystemProfile | null;
  isService: boolean;
  suspendScope: 'system' | 'tenant' | null;   // 暫停層級（MaintenancePage 依此顯不同訊息）
  isLoading: boolean;
  refreshSystemProfile: () => Promise<void>;
  getSystemTitle: () => string;
  getCopyright: () => string;
  getLoginBg: () => string;
  getColorTheme: () => string;
  getLayoutMode: () => string; // 'vertical' | 'horizontal'
  // 租戶 branding（主題/版面/logo/背景/favicon/時區/系統名稱）
  tenantBranding: TenantBranding | null;
  loadTenantBranding: () => Promise<void>;   // 儲存/上傳後 re-fetch 即時生效
  // 語系相關
  availableLanguages: LanguageOption[];
  defaultLanguage: string;
  // 系統訊息代碼對應（支援參數替換）
  getMessageByCode: (code: string, params?: Record<string, string>) => string;
  // 授權資訊與功能檢查
  license: LicenseInfo | null;
  hasFeature: (feature: string) => boolean;
}

const SystemContext = createContext<SystemContextType | undefined>(undefined);

interface SystemProviderProps {
  children: ReactNode;
}

export const SystemProvider: React.FC<SystemProviderProps> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [systemProfile, setSystemProfile] = useState<SystemProfile | null>(null);
  const [isService, setIsService] = useState(true);
  const [suspendScope, setSuspendScope] = useState<'system' | 'tenant' | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 租戶品牌標題（依 hostname 動態載入，優先於全平台 sys_title；公開端點，登入前即生效）
  const [tenantTitle, setTenantTitle] = useState<I18nField | null>(null);
  // 租戶完整 branding（主題/版面/logo/背景/favicon/時區）
  const [tenantBranding, setTenantBranding] = useState<TenantBranding | null>(null);

  // 語系狀態
  const [availableLanguages, setAvailableLanguages] = useState<LanguageOption[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('zh-TW');

  // 系統訊息代碼對應表（從 system_codes 的 sys_message_code 載入）
  const [messageCodes, setMessageCodes] = useState<Record<string, string>>({});

  // 授權資訊
  const [license, setLicense] = useState<LicenseInfo | null>(null);

  // 載入系統訊息代碼對應表（公開端點，不需認證）
  const loadMessageCodes = async (lang?: string) => {
    try {
      const currentLang = lang || i18n.language || 'zh-TW';
      const response = await axios.get(`/api/system/message-codes?lang=${currentLang}`);
      setMessageCodes(response.data || {});
    } catch (error) {
      console.error('載入系統訊息代碼對應表失敗:', error);
    }
  };

  // 載入啟用語系（公開端點，不需認證）
  const loadActiveLanguages = async () => {
    try {
      const response = await axios.get('/api/system/languages');
      const data = response.data;
      if (data.languages && Array.isArray(data.languages)) {
        setAvailableLanguages(data.languages);
      }
      if (data.default_language) {
        setDefaultLanguage(data.default_language);
      }
    } catch (error) {
      console.error('載入語系資訊失敗:', error);
      // fallback: 使用預設的 zh-TW 和 en
      setAvailableLanguages([
        { code: 'zh-TW', cname: '繁體中文', ename: 'Traditional Chinese', short_name: '繁中' },
        { code: 'en', cname: '英文', ename: 'English', short_name: 'EN' },
      ]);
    }
  };

  // 依 hostname 載入租戶品牌（公開端點，不需認證；覆蓋全平台 sys_title）
  const loadTenantBrand = async () => {
    try {
      const hostname = window.location.hostname;
      const brand = await systemService.getTenantBrand(hostname);
      if (brand?.tenant_title && Object.keys(brand.tenant_title).length > 0) {
        setTenantTitle(brand.tenant_title);
      }
      // tenant_title 空 → 保留 null，document.title 走 sys_title（平台 default fallback）
    } catch (err) {
      // 404（非租戶 hostname）或網路錯誤 → 不 crash，維持 sys_title
      console.warn('Tenant brand lookup failed:', err);
    }
  };

  // 依 Host 載入租戶完整 branding（公開端點；主題/版面/logo/背景/favicon/時區/系統名稱）
  const loadTenantBranding = async () => {
    try {
      const resp = await axios.get('/api/system/tenant-branding');
      setTenantBranding(resp.data);
      // 租戶時區（organizations.timezone，tenant_sys_profile 模組設定）灌進全域顯示時區，
      // 讓 dateHelper / 日曆等一律以「租戶自己的時區」呈現與反解（porsche 2026-08-28）。
      if (resp.data?.timezone) setDisplayTimeZone(resp.data.timezone);
      // Layer 2：租戶服務被暫停 → 顯 MaintenancePage（tenant-branding 為 bypass 端點，暫停時仍可讀）
      if (resp.data?.is_service_active === false) {
        setIsService(false);
        setSuspendScope('tenant');
      }
    } catch (err) {
      console.warn('Tenant branding lookup failed:', err);   // 不 crash，維持平台預設
    }
  };

  // 載入系統設定
  const loadSystemProfile = async () => {
    setIsLoading(true);
    try {
      // 載入基本系統狀態（公開端點·含 sys_title / sys_copyright / layout / color / 語系）
      // E6b-c：不再呼叫平台管理專用的 GET /api/sys_profiles/（一般使用者 enforce 後會 403）；
      // 前端需要的顯示欄位（sys_title / sys_copyright）此公開端點已提供。
      const profile = await systemService.getProfile();
      setSystemProfile(profile);
      setIsService(profile.is_service);
      if (profile.is_service === false) setSuspendScope('system');   // Layer 1 全系統維護
    } catch (error: any) {
      console.error('載入系統設定失敗:', error);
      // 只有 API 明確回傳維護狀態才顯示維護頁
      // 網路錯誤（後端暫時不可用）不應顯示維護頁
      if (error?.response?.data?.is_service === false) {
        setIsService(false);
        setSuspendScope('system');
      }
      // 網路錯誤時保留 isService=true（預設值），顯示正常頁面
    } finally {
      setIsLoading(false);
    }
  };

  // 重新載入系統設定
  const refreshSystemProfile = async () => {
    await loadSystemProfile();
    await loadActiveLanguages();
    await loadMessageCodes();
  };

  // 取得系統標題（優先序：tenantBranding.system_name → tenantTitle → 全平台 sys_title）
  const getSystemTitle = (): string => {
    if (tenantBranding?.system_name && Object.keys(tenantBranding.system_name).length > 0) {
      return getI18nValue(tenantBranding.system_name, i18n.language);
    }
    if (tenantTitle && Object.keys(tenantTitle).length > 0) {
      return getI18nValue(tenantTitle, i18n.language);
    }
    if (!systemProfile) return '';
    return getI18nValue(systemProfile.sys_title, i18n.language);
  };

  // 取得版權宣告（tenantBranding 優先）
  const getCopyright = (): string => {
    if (tenantBranding?.tenant_copyright && Object.keys(tenantBranding.tenant_copyright).length > 0) {
      return getI18nValue(tenantBranding.tenant_copyright, i18n.language);
    }
    if (!systemProfile) return '';
    return getI18nValue(systemProfile.sys_copyright, i18n.language);
  };

  // 取得登入頁底圖代碼
  const getLoginBg = (): string => {
    return systemProfile?.login_bg || 'default';
  };

  // 取得內頁配色主題
  const getColorTheme = (): string => {
    return systemProfile?.color_theme || 'classic-blue';
  };

  // 取得版面模式（tenantBranding 優先；vertical = 直式側邊欄, horizontal = 橫式頂部導覽）
  const getLayoutMode = (): string => {
    return tenantBranding?.layout_mode || systemProfile?.layout_mode || 'vertical';
  };

  // 取得系統訊息代碼對應的格式化訊息：「系統訊息：(代碼)說明」
  // 支援參數替換：getMessageByCode("SYS100001", { name: "使用者設定" })
  const getMessageByCode = useCallback((code: string, params?: Record<string, string>): string => {
    const label = t('common.systemMessage', '系統訊息');
    let description = messageCodes[code] || code;
    // 替換 {參數名} 佔位符
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        description = description.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
      });
    }
    return `${label}：(${code})${description}`;
  }, [messageCodes, t]);

  // 載入授權資訊（公開端點）
  const loadLicense = async () => {
    try {
      const info = await systemService.getLicense();
      setLicense(info);
    } catch (error) {
      console.error('載入授權資訊失敗:', error);
    }
  };

  // 檢查某個 EE 功能是否啟用
  const hasFeature = (feature: string): boolean => {
    return license?.features?.includes(feature) ?? false;
  };

  // 初始化：載入系統設定 + 語系資訊 + 錯誤代碼 + 授權 + 租戶品牌
  useEffect(() => {
    loadTenantBrand();
    loadTenantBranding();
    loadSystemProfile();
    loadActiveLanguages();
    loadMessageCodes();
    loadLicense();
  }, []);

  // axios 攔截器偵測 503 + X-Service-Suspended header（mid-session 被暫停）→ 接手顯 MaintenancePage
  useEffect(() => onSuspend((scope) => {
    setIsService(false);
    setSuspendScope(scope);
  }), []);

  // 動態切換瀏覽器 favicon（租戶 favicon_file_id → public branding-image）
  useEffect(() => {
    if (tenantBranding?.favicon_file_id) {
      const link = document.getElementById('dynamic-favicon') as HTMLLinkElement | null;
      if (link) link.href = `/api/system/branding-image/${tenantBranding.favicon_file_id}`;
    }
  }, [tenantBranding?.favicon_file_id]);

  // 套用配色主題到 document（租戶 theme_color 優先 → 儲存後即時切色）
  useEffect(() => {
    const theme = tenantBranding?.theme_color || systemProfile?.color_theme || 'classic-blue';
    document.documentElement.setAttribute('data-theme', theme);
  }, [systemProfile, tenantBranding?.theme_color]);

  // 若目前語系不在租戶啟用語系內 → 切到租戶第一個啟用語系
  useEffect(() => {
    const langs = tenantBranding?.tenant_languages;
    if (langs && langs.length > 0 && !langs.includes(i18n.language)) {
      i18n.changeLanguage(langs[0]);
    }
  }, [tenantBranding?.tenant_languages, i18n.language, i18n]);

  // 更新網頁分頁標題（瀏覽器標題·優先序：租戶瀏覽器標題 tenantTitle → 系統名稱 system_name → 平台 sys_title）
  //   tenant_title＝瀏覽器分頁/分享預覽（僅平台管理員改）；system_name＝頁首/首頁（角色 1/3/11 改）。
  //   唯一設定 document.title 處（原 useDocumentTitle 會與此互蓋·已移除）。+ 語系變更重載訊息代碼
  useEffect(() => {
    if (tenantTitle && Object.keys(tenantTitle).length > 0) {
      document.title = getI18nValue(tenantTitle, i18n.language);
    } else if (tenantBranding?.system_name && Object.keys(tenantBranding.system_name).length > 0) {
      document.title = getI18nValue(tenantBranding.system_name, i18n.language);
    } else if (systemProfile) {
      document.title = getI18nValue(systemProfile.sys_title, i18n.language);
    }
    loadMessageCodes(i18n.language);
  }, [i18n.language, systemProfile, tenantTitle, tenantBranding?.system_name]);

  // 語系選單依租戶啟用語系 filter（登入後 tenantBranding 有值時只顯 tenant 啟用的）
  const filteredLanguages = tenantBranding?.tenant_languages?.length
    ? availableLanguages.filter((l) => tenantBranding.tenant_languages.includes(l.code))
    : availableLanguages;

  return (
    <SystemContext.Provider
      value={{
        systemProfile,
        isService,
        suspendScope,
        isLoading,
        refreshSystemProfile,
        getSystemTitle,
        getCopyright,
        getLoginBg,
        getColorTheme,
        getLayoutMode,
        tenantBranding,
        loadTenantBranding,
        availableLanguages: filteredLanguages,
        defaultLanguage,
        getMessageByCode,
        license,
        hasFeature,
      }}
    >
      {children}
    </SystemContext.Provider>
  );
};

// 自訂 Hook
export const useSystem = () => {
  const context = useContext(SystemContext);
  if (context === undefined) {
    throw new Error('useSystem must be used within a SystemProvider');
  }
  return context;
};
